import { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  MessageSquare,
  Megaphone,
  ImageIcon,
  Layers,
  Film,
  Code2,
  Home as HomeIcon,
  Menu,
  X,
  ShieldCheck,
  ShieldAlert,
  Mic,
  Clapperboard,
  PlaySquare,
  Box,
  Info,
} from "lucide-react";
import Logo from "./Logo.jsx";
import AdUnit from "./AdUnit.jsx";
import { getHealth, warmApi } from "../lib/api.js";
import { LANGUAGE_OPTIONS, useI18n } from "../lib/i18n.jsx";

const NAV = [
  { to: "/", labelKey: "nav.home", icon: HomeIcon, end: true },
  { to: "/chat", labelKey: "nav.chat", icon: MessageSquare },
  { to: "/marketing", labelKey: "nav.marketing", icon: Megaphone },
  { to: "/image", labelKey: "nav.image", icon: ImageIcon },
  { to: "/image-studio", labelKey: "nav.imageStudio", icon: Layers },
  { to: "/video", labelKey: "nav.video", icon: Film, comingSoon: true },
  { to: "/tts", labelKey: "nav.tts", icon: Mic },
  { to: "/studio", labelKey: "nav.studio", icon: Clapperboard },
  { to: "/shorts", labelKey: "nav.shortsStudio", icon: PlaySquare },
  { to: "/model3d", labelKey: "nav.model3d", icon: Box },
  { to: "/code", labelKey: "nav.code", icon: Code2 },
  { to: "/about", labelKey: "nav.about", icon: Info },
];

function NavItem({ to, label, icon: Icon, end, onClick, comingSoon }) {
  const { t } = useI18n();
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition",
          isActive
            ? "bg-consensus-500/10 text-consensus-300 border border-consensus-500/20 shadow-glow"
            : "text-white/70 hover:text-white hover:bg-white/5 border border-transparent",
        ].join(" ")
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{label}</span>
      {comingSoon && (
        <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-200/90 border border-amber-500/20">
          {t("common.soon", "Soon")}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { t, language, setLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    warmApi();
    getHealth()
      .then(setHealth)
      .catch(() => setHealth({ ok: false, hasKey: false }));
  }, []);

  return (
    <div className="min-h-full flex flex-col md:flex-row">
      {/* Mobile top bar */}
      <header className="md:hidden flex items-center justify-between px-4 h-14 border-b border-white/5 bg-ink-900/80 backdrop-blur sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-2">
          <Logo className="h-7 w-7" />
          <span className="font-bold tracking-tight">
            The Consensus{" "}
            <span className="bg-brand-gradient bg-clip-text text-transparent">
              A.I.
            </span>
          </span>
        </Link>
        <button
          className="btn-ghost p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </header>

      {/* Sidebar */}
      <aside
        className={[
          "md:w-64 md:shrink-0 md:sticky md:top-0 md:h-screen",
          "border-r border-white/5 bg-ink-900/60 backdrop-blur-xl",
          "flex flex-col",
          open ? "block" : "hidden md:flex",
        ].join(" ")}
      >
        <div className="hidden md:flex items-center gap-3 px-5 h-16 border-b border-white/5">
          <Logo className="h-9 w-9" />
          <div className="leading-tight">
            <div className="font-bold tracking-tight text-[15px]">
              The Consensus
            </div>
            <div className="text-xs font-semibold tracking-widest bg-brand-gradient bg-clip-text text-transparent">
              A.I.
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              label={t(item.labelKey, item.labelKey)}
              onClick={() => setOpen(false)}
            />
          ))}
        </nav>

        <div className="px-3 pb-2">
          <AdUnit
            slot={import.meta.env.VITE_ADSENSE_SLOT_SIDEBAR}
            className="border border-white/5 bg-white/[0.02] p-2"
          />
        </div>

        <div className="p-3 border-t border-white/5 space-y-2">
          <label className="flex items-center justify-between gap-2 text-xs text-white/60">
            <span>{t("layout.language.label", "Language")}</span>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="input !w-auto !py-1.5 !text-xs"
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div
            className={[
              "flex items-center gap-2 text-xs rounded-lg px-3 py-2 border",
              health?.hasKey
                ? "border-consensus-500/20 bg-consensus-500/5 text-consensus-300"
                : "border-white/10 bg-white/[0.03] text-white/50",
            ].join(" ")}
          >
            {health?.hasKey ? (
              <ShieldCheck className="h-3.5 w-3.5" />
            ) : (
              <ShieldAlert className="h-3.5 w-3.5" />
            )}
            <span className="font-medium">
              {health?.hasKey
                ? t("layout.health.ready", "NVIDIA NIM ready")
                : t("layout.health.missing", "NIM key missing")}
            </span>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <div className="flex-1 min-w-0 animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
